import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import {
  GET_ORDERS,
  GET_CUSTOMER_ORDERS,
  GetOrdersData,
  GetOrdersVariables,
  OrderNode,
} from '../graphql/queries/orders';
import { useAuth } from '../contexts/AuthContext';
import { filterOwnOrders } from '../utils/customerAccess';

interface UseScopedOrdersOptions {
  first?: number;
  paginateAll?: boolean;
}

export function useScopedOrders(options: UseScopedOrdersOptions = {}) {
  const { first = 100, paginateAll = false } = options;
  const { user, loading: authLoading, isCustomerPortalUser } = useAuth();
  const [fetchingMore, setFetchingMore] = useState(false);
  const scoped = isCustomerPortalUser();
  const customerId = scoped ? user?.databaseId : undefined;
  const where = customerId ? { customerId } : undefined;

  const queryDoc = scoped && customerId ? GET_CUSTOMER_ORDERS : GET_ORDERS;

  const { data, loading, error, refetch, fetchMore } = useQuery<
    GetOrdersData,
    GetOrdersVariables
  >(queryDoc, {
    variables: scoped && customerId ? { first, where } : { first },
    skip: authLoading,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const pageInfo = data?.orders?.pageInfo;
  const hasNextPage = paginateAll ? pageInfo?.hasNextPage ?? false : false;

  useEffect(() => {
    if (!paginateAll || !hasNextPage || !pageInfo?.endCursor || fetchingMore) return;

    let cancelled = false;
    setFetchingMore(true);

    fetchMore({
      variables:
        scoped && customerId
          ? { after: pageInfo.endCursor, where }
          : { after: pageInfo.endCursor },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.orders) return previousResult;
        return {
          orders: {
            ...fetchMoreResult.orders,
            nodes: [
              ...previousResult.orders.nodes,
              ...fetchMoreResult.orders.nodes,
            ],
          },
        };
      },
    })
      .then(() => {
        if (!cancelled) setFetchingMore(false);
      })
      .catch(() => {
        if (!cancelled) setFetchingMore(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    paginateAll,
    hasNextPage,
    pageInfo?.endCursor,
    data?.orders?.nodes?.length,
    fetchMore,
    fetchingMore,
    customerId,
    scoped,
  ]);

  const orders: OrderNode[] = useMemo(() => {
    const nodes = data?.orders?.nodes ?? [];
    if (!scoped) return nodes;
    return filterOwnOrders(nodes, user);
  }, [data?.orders?.nodes, scoped, user]);

  const isLoading =
    authLoading ||
    (loading && orders.length === 0) ||
    fetchingMore ||
    (paginateAll && hasNextPage);

  return {
    orders,
    data,
    loading: isLoading,
    error,
    refetch,
    fetchMore,
  };
}
